import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { ContractSupplementDataType, type ContractSupplementChange } from '../entities/ContractSupplement.js';

function parseJson(
  change: NarrowedEntity<ContractSupplementChange, { value: 1; type: 1 }>,
  constructComplexValues = false,
) {
  if (!change.value) {
    return null;
  }
  let jsonValue = JSON.parse(change.value);

  // if the json is an array we potentially need to construct inner values
  if (Array.isArray(jsonValue)) {
    jsonValue = jsonValue.map(v => (v!!._class && constructComplexValues ? constructValue(v) : v));
  }

  return jsonValue._class && constructComplexValues ? constructValue(jsonValue) : jsonValue;
}

/**
 * Parses the value of a nachtrag change to the correct type
 * @param change
 * @param constructComplexValues sometimes we do not want to construct adornisentitis as a change might conflict with the entity store on the client. A contract has the same entity with the id as the change but the change hold another value and we just need raw data anyway.
 * @returns
 */
export function getParsedValueOfSupplementChange(
  change: NarrowedEntity<ContractSupplementChange, { value: 1; type: 1 }>,
  constructComplexValues = false,
) {
  switch (change.type) {
    case ContractSupplementDataType.DATE:
      return new Date(change.value);
    case ContractSupplementDataType.STRING:
      return change.value;
    case ContractSupplementDataType.NUMBER: {
      const parsed = parseFloat(change.value);
      // legacy changes carry "null"/"NaN" as number values - NaN would silently poison the hours/percent math
      return Number.isNaN(parsed) ? null : parsed;
    }
    case ContractSupplementDataType.BOOLEAN:
      return change.value === 'true';
    case ContractSupplementDataType.NULL:
      return null;
    case ContractSupplementDataType.NEW_FIELD:
      if (change.value === 'null' || change.value === '[]') return null;

      // catch weird behavior where a json array is parsed as string
      if (!change.value.startsWith('[{')) return change.value;
      return parseJson(change, constructComplexValues);
    case ContractSupplementDataType.JSON:
      return parseJson(change, constructComplexValues);
    default:
      return change.value;
  }
}
